import { ActionTree } from 'vuex';
import { IDirectoryTree } from '../types/directory-tree';
import { DirectoryTreeHelper } from '../helper/directory-tree.helper';
import {
  EDirectoryTreeAction,
  EDirectoryTreeMutation,
  state as defaultState,
} from './directory-tree.constants';
import { IDirectoryTreeState } from './directory-tree.types';
import { RootState } from '@/store/store';
import { IDirectory } from '@/api/directories/types/directory';
import { DirectoryService } from '@/api/directories/services/directory.service';
import { translate } from '@/helpers/translation.helper';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';

export const actions: ActionTree<IDirectoryTreeState, RootState> = {
  async [EDirectoryTreeAction.LoadRoot]({ commit, dispatch }, path: string) {
    commit(EDirectoryTreeMutation.SetLoading, true);
    commit(EDirectoryTreeMutation.SetLoaded, false);
    try {
      const directories: IDirectory[] = await DirectoryService.loadDirectories(path);
      const subDirectories: IDirectory[][] = await Promise.all(
        directories.map(async (item: IDirectory) =>
          DirectoryService.loadDirectories(`${item.directory.replace(/^\/+/, '')}/${item.title}/`)
        )
      );
      const tree: IDirectoryTree = {
        id: '',
        title: '',
        directory: '',
        children: [],
      };

      tree.children = DirectoryTreeHelper.buildDirectoryTreeWithChildren(
        directories,
        subDirectories
      );

      dispatch(EDirectoryTreeAction.LoadRootSuccess, tree);
    } catch {
      dispatch(EDirectoryTreeAction.LoadRootFailed);
    }
  },
  [EDirectoryTreeAction.LoadRootSuccess]({ commit }, tree: IDirectoryTree) {
    commit(EDirectoryTreeMutation.SetDirectoryTree, tree);
    commit(EDirectoryTreeMutation.SetLoading, false);
    commit(EDirectoryTreeMutation.SetLoaded, true);
  },
  [EDirectoryTreeAction.LoadRootFailed]({ commit, dispatch }) {
    commit(EDirectoryTreeMutation.SetLoading, false);
    commit(EDirectoryTreeMutation.SetLoaded, false);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-load-folders'), {
      root: true,
    });
  },
  async [EDirectoryTreeAction.LoadSub]({ dispatch, state }, path: string) {
    try {
      const { tree } = state;
      const directories: IDirectory[] = await DirectoryService.loadDirectories(path);
      const subDirectories: IDirectory[][] = await Promise.all(
        directories.map(async (item: IDirectory) =>
          DirectoryService.loadDirectories(`${item.directory}${item.title}/`)
        )
      );
      const newChildren: IDirectoryTree[] = DirectoryTreeHelper.buildDirectoryTreeWithChildren(
        directories,
        subDirectories
      );

      newChildren.forEach((child: IDirectoryTree) => {
        tree.children = DirectoryTreeHelper.buildDirectoryTree(tree.children, child);
      });

      dispatch(EDirectoryTreeAction.LoadSubSuccess, tree);
    } catch {
      dispatch(EDirectoryTreeAction.LoadSubFailed);
    }
  },
  [EDirectoryTreeAction.LoadSubSuccess]({ commit }, tree: IDirectoryTree) {
    commit(EDirectoryTreeMutation.SetDirectoryTree, tree);
  },
  [EDirectoryTreeAction.LoadSubFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-load-folders'), {
      root: true,
    });
  },
  [EDirectoryTreeAction.Select]({ commit }, selected: string) {
    commit(EDirectoryTreeMutation.SetSelected, selected);
  },
  [EDirectoryTreeAction.Reset]({ commit }) {
    commit(EDirectoryTreeMutation.SetState, defaultState);
  },
};
